import React, { cloneElement, isValidElement, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** Turn very long single-line copy into Markdown bullets so lists render with icons. */
function breakLongRunOnLinesIntoBullets(text) {
  const lines = text.split("\n");
  const out = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (
      !trimmed ||
      trimmed.startsWith("#") ||
      trimmed.startsWith("|") ||
      trimmed.startsWith("-") ||
      trimmed.startsWith("*") ||
      trimmed.startsWith("```") ||
      trimmed.length < 220
    ) {
      out.push(line);
      continue;
    }
    const sentences = trimmed.split(/(?<=[.!?])\s+(?=[A-Z0-9(•\-])/).filter((s) => s.length > 12);
    if (sentences.length >= 4) {
      if (out.length && out[out.length - 1] !== "") out.push("");
      sentences.forEach((s) => out.push(`- ${s.trim()}`));
      out.push("");
    } else {
      out.push(line);
    }
  }
  return out.join("\n");
}

/** Fixes common paste mistakes so Markdown parses (e.g. `##Title` → `## Title`). */
function normalizeDescriptionMarkdown(raw) {
  let t = String(raw || "").replace(/\r\n/g, "\n").trim();
  t = t.replace(/^(#{1,6})([^\s#])/gm, "$1 $2");
  t = t.replace(/^(\s*)-([^\s\n-])/gm, "$1- $2");
  t = t.replace(/^(\s*)\*([^\s\n*])/gm, "$1* $2");
  t = breakLongRunOnLinesIntoBullets(t);
  return t;
}

/** Split `##` intro / tables from each `###` block so we can lay out 2+ feature cards side by side. */
function splitIntroAndH3Sections(text) {
  const t = text.trim();
  const match = t.match(/^###\s/m);
  if (!match || match.index === undefined) {
    return { intro: t, sections: [] };
  }
  const intro = t.slice(0, match.index).trim();
  const rest = t.slice(match.index);
  const sections = rest
    .split(/(?=^###\s)/m)
    .map((s) => s.trim())
    .filter(Boolean);
  return { intro, sections };
}

function IconCheck() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function IconPackage() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16.5 9.4 7.55 4.24" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96 12 12.01l8.73-5.05" />
      <path d="M12 22.08V12" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

const LI_ICONS = [IconCheck, IconPackage, IconShield];

function FloatingBoxIllustration() {
  return (
    <div className="premium-desc-box-wrap">
      <div className="premium-desc-box-glow" aria-hidden />
      <svg className="premium-desc-box-svg" viewBox="0 0 220 240" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <defs>
          <linearGradient id="premiumBoxTop" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="premiumBoxSide" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#1e293b" />
            <stop offset="100%" stopColor="#020617" />
          </linearGradient>
          <linearGradient id="premiumBoxFront" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#334155" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
          <filter id="premiumBoxBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="8" result="b" />
          </filter>
        </defs>
        <ellipse cx="110" cy="210" rx="72" ry="14" fill="#38bdf8" fillOpacity="0.12" filter="url(#premiumBoxBlur)" />
        <g className="premium-desc-box-group">
          <path d="M110 32 L188 78 L188 158 L110 204 L32 158 L32 78 Z" fill="url(#premiumBoxTop)" stroke="rgba(56,189,248,0.35)" strokeWidth="1.2" />
          <path d="M32 78 L110 124 L110 204 L32 158 Z" fill="url(#premiumBoxSide)" stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
          <path d="M110 124 L188 78 L188 158 L110 204 Z" fill="url(#premiumBoxFront)" stroke="rgba(56,189,248,0.28)" strokeWidth="1" />
          <path d="M110 32 L110 124" stroke="rgba(56,189,248,0.4)" strokeWidth="1" strokeDasharray="4 6" />
        </g>
      </svg>
    </div>
  );
}

function buildPremiumComponents() {
  return {
    h2: ({ children, ...props }) => (
      <h2 className="premium-h2" {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className="premium-h3" {...props}>
        {children}
      </h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 className="premium-h4" {...props}>
        {children}
      </h4>
    ),
    p: ({ children, ...props }) => (
      <p className="premium-p" {...props}>
        {children}
      </p>
    ),
    strong: ({ children, ...props }) => (
      <strong className="premium-strong" {...props}>
        {children}
      </strong>
    ),
    a: ({ children, ...props }) => (
      <a className="premium-a" target="_blank" rel="noreferrer" {...props}>
        {children}
      </a>
    ),
    hr: (props) => <hr className="premium-hr" {...props} />,
    table: ({ children, ...props }) => (
      <div className="premium-table-scroll">
        <table className="premium-table" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => <thead {...props}>{children}</thead>,
    tbody: ({ children, ...props }) => <tbody {...props}>{children}</tbody>,
    tr: ({ children, ...props }) => <tr {...props}>{children}</tr>,
    th: ({ children, ...props }) => (
      <th className="premium-th" {...props}>
        {children}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className="premium-td" {...props}>
        {children}
      </td>
    ),
    ul: ({ children, ...props }) => (
      <ul className="premium-ul" {...props}>
        {React.Children.map(children, (child, index) => {
          if (!isValidElement(child)) return child;
          return cloneElement(child, { "data-premium-li-idx": index });
        })}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol className="premium-ul premium-ol" {...props}>
        {React.Children.map(children, (child, index) => {
          if (!isValidElement(child)) return child;
          return cloneElement(child, { "data-premium-li-idx": index });
        })}
      </ol>
    ),
    li: ({ children, node, className, ...props }) => {
      const idx = Number(props["data-premium-li-idx"] ?? 0) || 0;
      const Icon = LI_ICONS[idx % LI_ICONS.length];
      const { "data-premium-li-idx": _d, ...domProps } = props;
      return (
        <li
          className={`premium-feature-li${className ? ` ${className}` : ""}`.trim()}
          style={{ "--premium-stagger": idx }}
          {...domProps}
        >
          <span className="premium-li-icon" aria-hidden>
            <Icon />
          </span>
          <span className="premium-li-body">{children}</span>
        </li>
      );
    }
  };
}

const premiumMarkdownComponents = buildPremiumComponents();

/**
 * @param {object} props
 * @param {string} props.text
 * @param {string} [props.className]
 * @param {"premium"|"classic"} [props.variant="premium"]
 */
export default function ProductDescription({ text, className = "", variant = "premium" }) {
  const src = normalizeDescriptionMarkdown(text);
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (variant !== "premium") return;
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.06, rootMargin: "0px 0px -32px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [variant, src]);

  if (!src) {
    if (variant === "classic") {
      return (
        <div className={`product-description-rich ${className}`.trim()}>
          <p>Premium quality product with durable build.</p>
          <ul>
            <li>Ideal for gifting and daily use</li>
            <li>Carefully packed and quality checked</li>
          </ul>
        </div>
      );
    }
    return (
      <div ref={rootRef} className={`premium-desc-root ${visible ? "premium-desc-root--visible" : ""} ${className}`.trim()}>
        <div className="premium-desc-bg" aria-hidden>
          <div className="premium-desc-orb premium-desc-orb--a" />
          <div className="premium-desc-orb premium-desc-orb--b" />
        </div>
        <div className="premium-desc-layout">
          <div className="premium-desc-main">
            <div className="product-description-premium">
              <p className="premium-p">Premium quality product with durable build.</p>
              <div className="premium-section-grid">
                <div className="premium-section-card">
                  <ul className="premium-ul">
                    <li className="premium-feature-li" style={{ "--premium-stagger": 0 }}>
                      <span className="premium-li-icon" aria-hidden>
                        <IconCheck />
                      </span>
                      <span className="premium-li-body">Ideal for gifting and daily use</span>
                    </li>
                    <li className="premium-feature-li" style={{ "--premium-stagger": 1 }}>
                      <span className="premium-li-icon" aria-hidden>
                        <IconPackage />
                      </span>
                      <span className="premium-li-body">Carefully packed and quality checked</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <aside className="premium-desc-aside" aria-hidden>
            <FloatingBoxIllustration />
          </aside>
        </div>
      </div>
    );
  }

  if (variant === "classic") {
    return (
      <div className={`product-description-rich ${className}`.trim()}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{src}</ReactMarkdown>
      </div>
    );
  }

  const { intro, sections } = splitIntroAndH3Sections(src);

  return (
    <div ref={rootRef} className={`premium-desc-root ${visible ? "premium-desc-root--visible" : ""} ${className}`.trim()}>
      <div className="premium-desc-bg" aria-hidden>
        <div className="premium-desc-orb premium-desc-orb--a" />
        <div className="premium-desc-orb premium-desc-orb--b" />
        <div className="premium-desc-line" />
      </div>
      <div className="premium-desc-layout">
        <div className="premium-desc-main">
          <div className="product-description-premium">
            {intro ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={premiumMarkdownComponents}>
                {intro}
              </ReactMarkdown>
            ) : null}
            {sections.length > 0 ? (
              <div className={`premium-section-grid premium-section-grid--${Math.min(sections.length, 3)}`}>
                {sections.map((chunk, i) => (
                  <div key={`sec-${i}`} className="premium-section-card">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={premiumMarkdownComponents}>
                      {chunk}
                    </ReactMarkdown>
                  </div>
                ))}
              </div>
            ) : (
              !intro && (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={premiumMarkdownComponents}>
                  {src}
                </ReactMarkdown>
              )
            )}
          </div>
        </div>
        <aside className="premium-desc-aside" aria-hidden>
          <FloatingBoxIllustration />
        </aside>
      </div>
    </div>
  );
}
